import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  ApiResponse,
  LeaveRequestListItem,
  LeaveRequestListResponse,
  LeaveRequestLookupItem,
  LeaveRequestEmployeeItem,
  EmployeeContractLeaveBalance,
  LeaveRequestVm,
  LeaveRequestSavePayload,
} from '@/api/types'

function normalizeLookup(item: any): LeaveRequestLookupItem {
  const id = item.id ?? item.Id ?? 0
  const lookupCodeId = item.lookupCodeId ?? item.LookupCodeId
  return {
    id,
    lookupCodeId: lookupCodeId != null ? Number(lookupCodeId) : undefined,
    name: item.name ?? item.Name ?? item.lookupNameEn ?? item.LookupNameEn ?? item.nameEn ?? item.NameEn,
    nameEn: item.nameEn ?? item.NameEn ?? item.lookupNameEn ?? item.LookupNameEn,
    nameAr: item.nameAr ?? item.NameAr ?? item.lookupNameAr ?? item.LookupNameAr,
    lookupNameEn: item.lookupNameEn ?? item.LookupNameEn,
    lookupNameAr: item.lookupNameAr ?? item.LookupNameAr,
    codeType: item.codeType ?? item.CodeType,
  }
}

function normalizeListItem(row: any): LeaveRequestListItem {
  return {
    id: row.id ?? row.Id ?? 0,
    empId: row.empId ?? row.EmpId,
    leaveTypeId: row.leaveTypeId ?? row.LeaveTypeId,
    leaveTypeName: row.leaveTypeName ?? row.LeaveTypeName,
    status: row.status ?? row.Status,
    statusName: row.statusName ?? row.StatusName,
    /** API often sends dateFrom/dateTo (Swagger) */
    fromDate:
      row.dateFrom ??
      row.DateFrom ??
      row.fromDate ??
      row.FromDate ??
      null,
    toDate:
      row.dateTo ??
      row.DateTo ??
      row.toDate ??
      row.ToDate ??
      null,
    /** API often sends substituteId */
    substituteEmpId:
      row.substituteId ??
      row.SubstituteId ??
      row.substituteEmpId ??
      row.SubstituteEmpId,
    substituteName: row.substituteName ?? row.SubstituteName,
    approvedDate:
      row.approvalDate ??
      row.ApprovalDate ??
      row.approvedDate ??
      row.ApprovedDate ??
      null,
  }
}

class LeaveRequestService {
  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    codeType?: string
  ): Promise<LeaveRequestListResponse> {
    const url = endpoints.leaveRequest.getAll(branchId, pageNumber, pageSize, codeType)
    const response = await apiClient.get<ApiResponse<LeaveRequestListResponse> | any>(url)
    const payload = response.data
    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    if (raw && typeof raw === 'object') {
      const arr = Array.isArray(raw.data) ? raw.data : Array.isArray(raw) ? raw : []
      return {
        totalRecords: raw.totalRecords ?? payload.totalRecords ?? arr.length ?? 0,
        pageNumber: raw.pageNumber ?? payload.pageNumber ?? pageNumber,
        pageSize: raw.pageSize ?? payload.pageSize ?? pageSize,
        data: (arr as any[]).map(normalizeListItem),
      }
    }
    return { totalRecords: 0, pageNumber, pageSize, data: [] }
  }

  async getById(id: number, branchId: number): Promise<LeaveRequestVm | null> {
    const url = endpoints.leaveRequest.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = response.data
    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    if (!raw || typeof raw !== 'object') return null
    return {
      id: raw.id ?? raw.Id ?? 0,
      empId: raw.empId ?? raw.EmpId ?? 0,
      branchId: raw.branchId ?? raw.BranchId ?? branchId,
      leaveTypeId: raw.leaveTypeId ?? raw.LeaveTypeId ?? 0,
      substituteEmpId: raw.substituteEmpId ?? raw.SubstituteEmpId ?? 0,
      fromDate: raw.fromDate ? String(raw.fromDate).slice(0, 10) : '',
      toDate: raw.toDate ? String(raw.toDate).slice(0, 10) : '',
      details: raw.details ?? raw.Details ?? raw.reason ?? raw.Reason ?? '',
      status: raw.status ?? raw.Status ?? 0,
      approvalDate: raw.approvalDate ?? raw.ApprovalDate ?? null,
      isEditable: raw.isEditable ?? raw.IsEditable ?? 0,
    }
  }

  async getLeaveTypes(): Promise<LeaveRequestLookupItem[]> {
    const url = endpoints.leaveRequest.getLeaveTypes
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map(normalizeLookup)
  }

  async getStatusLookups(): Promise<LeaveRequestLookupItem[]> {
    const url = endpoints.leaveRequest.getStatusLookups
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map(normalizeLookup)
  }

  async getActiveEmployees(branchId: number): Promise<LeaveRequestEmployeeItem[]> {
    const url = endpoints.leaveRequest.getActiveEmployees(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map((e: any) => {
      const nameFromParts = [e.empFirstNameEn ?? e.EmpFirstNameEn, e.empLastNameEn ?? e.EmpLastNameEn]
        .filter(Boolean)
        .join(' ')
        .trim()
      const fallbackName = e.employeeName ?? e.EmployeeName
      const fullName = e.fullName ?? e.FullName ?? (nameFromParts || fallbackName)
      return {
        id: e.id ?? e.Id ?? e.empId ?? e.EmpId ?? 0,
        empId: e.empId ?? e.EmpId ?? e.id ?? e.Id,
        fullName,
      }
    })
  }

  async getEmployeeContract(empId: number, branchId: number): Promise<EmployeeContractLeaveBalance | null> {
    const url = endpoints.leaveRequest.getEmployeeContract(empId, branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = response.data
    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    if (!raw || typeof raw !== 'object') return null
    return {
      // API can return either camelCase (leaveBalance) or plural (leavesBalance / leavesAvailed)
      leaveBalance: raw.leaveBalance ?? raw.LeaveBalance ?? raw.leavesBalance ?? raw.LeavesBalance ?? 0,
      requestedLeave: raw.requestedLeave ?? raw.RequestedLeave ?? raw.leavesAvailed ?? raw.LeavesAvailed ?? 0,
      remainingLeave:
        raw.remainingLeave ??
        raw.RemainingLeave ??
        raw.leavesBalance ??
        raw.LeavesBalance ??
        0,
      empId: raw.empId ?? raw.EmpId ?? empId,
    }
  }

  async create(payload: LeaveRequestSavePayload): Promise<any> {
    console.log('Leave Request Save Payload:', payload)
    const response = await apiClient.post(endpoints.leaveRequest.create, payload)
    return response.data
  }

  async update(payload: LeaveRequestSavePayload): Promise<any> {
    console.log('Leave Request Save Payload:', payload)
    // Backend commonly expects POST (some environments return 404 on PUT)
    const response = await apiClient.post(endpoints.leaveRequest.update, payload)
    return response.data
  }

  async delete(id: number): Promise<any> {
    const url = endpoints.leaveRequest.delete(id)
    const response = await apiClient.delete(url)
    return response.data
  }
}

export const leaveRequestService = new LeaveRequestService()
